"use client"

import { 
  Box, 
  TextField, 
  MenuItem,
  Typography, 
  FormControl, 
  Select, 
  Card, 
  CardContent,
  Autocomplete,
  Button
} from "@mui/material"
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { es } from 'date-fns/locale'
import { useState, useEffect, useRef, ReactNode } from "react"

interface FilterOption {
  label: string
  value: string
  type?: 'select' | 'text' | 'search' | 'date' | 'custom'
  options?: { label: string; value: string }[]
  width?: number
  debounce?: number
  minDate?: Date
  maxDate?: Date
  component?: ReactNode
}

interface FiltersSectionProps {
  filters: FilterOption[]
  onFilterChange?: (filterId: string, value: string) => void
  values?: Record<string, string>
  onSearch?: () => void          // Callback para el botón de búsqueda
  searchButtonDisabled?: boolean // Estado disabled del botón
}

// Función para convertir string YYYY-MM-DD a Date local sin conversión UTC
const parseLocalDate = (dateString: string): Date | null => {
  if (!dateString) return null
  const [year, month, day] = dateString.split('-').map(Number)
  // Crear fecha usando valores locales (año, mes, día)
  return new Date(year, month - 1, day)
}

// Función para convertir Date local a string YYYY-MM-DD
const formatLocalDate = (date: Date | null): string => {
  if (!date) return ''
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export function FiltersSection({ 
  filters, 
  onFilterChange, 
  values = {},
  onSearch,
  searchButtonDisabled = false
}: FiltersSectionProps) {
  const [textValues, setTextValues] = useState<Record<string, string>>({})
  const [dateValues, setDateValues] = useState<Record<string, Date | null>>({})
  const debounceRef = useRef<NodeJS.Timeout>()

  useEffect(() => {
    const newTextValues: Record<string, string> = {};
    const newDateValues: Record<string, Date | null> = {};

    filters.forEach(filter => {
      if (filter.type === 'text') {
        newTextValues[filter.value] = values[filter.value] || '';
      }
      if (filter.type === 'search') {
        const currentValue = values[filter.value];
        if (currentValue && filter.options) {
          const selectedOption = filter.options.find(opt => opt.value === currentValue);
          newTextValues[filter.value] = selectedOption ? selectedOption.label : '';
        } else {
          newTextValues[filter.value] = '';
        }
      }
      if (filter.type === 'date') {
        const dateValue = values[filter.value];
        newDateValues[filter.value] = dateValue ? parseLocalDate(dateValue) : null;
      }
    });

    setTextValues(newTextValues);
    setDateValues(newDateValues);
  }, [values, filters]);

  const handleInputChange = (filterId: string, value: string) => {
    onFilterChange?.(filterId, value)
  }

  const handleTextChange = (filterId: string, value: string, debounceMs?: number) => {
    setTextValues(prev => ({ ...prev, [filterId]: value }))

    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }

    if (!debounceMs || debounceMs === 0) {
      onFilterChange?.(filterId, value)
      return
    }

    debounceRef.current = setTimeout(() => {
      onFilterChange?.(filterId, value)
    }, debounceMs)
  }

  const handleSearchChange = (filterId: string, newValue: { label: string; value: string } | null) => {
    const valueToEmit = newValue?.value || '';
    onFilterChange?.(filterId, valueToEmit);
    console.log('handleSearchChange', newValue)
    setTextValues(prev => ({ ...prev, [filterId]: newValue?.label || '' }));
  };

  const handleSearchInputChange = (filterId: string, value: string) => {
    setTextValues(prev => ({ ...prev, [filterId]: value }))
  }

  const handleDateChange = (filterId: string, date: Date | null) => {
    setDateValues(prev => ({ ...prev, [filterId]: date }))
    // Formatear la fecha como YYYY-MM-DD usando la fecha local
    const formattedDate = formatLocalDate(date)
    onFilterChange?.(filterId, formattedDate)
  }
  
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [])

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
      <Card sx={{ mb: 1 }}>
        <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            {filters.map((filter) => (
              <Box key={filter.value} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                  {filter.label}
                </Typography>
                
                {filter.type === 'custom' && filter.component && (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {filter.component}
                  </Box>
                )}

                {filter.type === 'text' && (
                  <TextField
                    size="small"
                    value={textValues[filter.value] || ""}
                    onChange={(e) => handleTextChange(filter.value, e.target.value, filter.debounce)}
                    sx={{ 
                      minWidth: 120,
                      '& .MuiInputBase-root': { 
                        height: '30px',
                        fontSize: '0.875rem'
                      }
                    }}
                    placeholder={`Buscar ${filter.label.toLowerCase()}...`}
                  />
                )}

                {filter.type === 'search' && (
                  <Autocomplete
                    size="small"
                    options={filter.options || []}
                    value={filter.options?.find(opt => opt.value === values[filter.value]) || null}
                    inputValue={textValues[filter.value] || ""}
                    onInputChange={(_, value) => handleSearchInputChange(filter.value, value)}
                    onChange={(_, newValue) => handleSearchChange(filter.value, newValue)}
                    getOptionLabel={(option) => option.label}
                    getOptionKey={(option) => option.value}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={`Buscar ${filter.label.toLowerCase()}...`}
                        sx={{ 
                          minWidth: 120,
                          width: filter.width,
                          '& .MuiInputBase-root': { 
                            height: '30px',
                            fontSize: '0.875rem'
                          }
                        }}
                      />
                    )}
                    sx={{ minWidth: 120 }}
                    filterOptions={(options, state) => {
                      if (!state.inputValue) {
                        return options
                      }
                      return options.filter(option =>
                        option.label.toLowerCase().includes(state.inputValue.toLowerCase())
                      )
                    }}
                  />
                )}

                {filter.type === 'date' && (
                  <DatePicker
                    value={dateValues[filter.value] || null}
                    onChange={(date) => handleDateChange(filter.value, date)}
                    minDate={filter.minDate}
                    maxDate={filter.maxDate}
                    // Configuración para trabajar con fechas locales
                    timezone="system"
                    // Deshabilitar cualquier transformación automática
                    disableOpenPicker={false}
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: { 
                          minWidth: 150,
                          '& .MuiInputBase-root': { 
                            height: '30px',
                            fontSize: '0.875rem'
                          }
                        },
                        placeholder: 'DD/MM/YYYY'
                      },
                      // Configurar el adaptador para usar hora local
                      field: {
                        format: 'dd/MM/yyyy',
                        shouldRespectLeadingZeros: true
                      }
                    }}
                  />
                )}

                {(filter.type === 'select' || !filter.type) && (
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <Select
                      value={values[filter.value] || ""}
                      onChange={(e) => handleInputChange(filter.value, e.target.value)}
                      sx={{ height: '30px' }}
                    >
                      {filter.options?.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Box>
            ))}

            {onSearch && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, justifyContent: 'flex-end' }}>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', visibility: 'hidden' }}>
                  .
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  onClick={onSearch}
                  disabled={searchButtonDisabled}
                  sx={{ height: '30px', minWidth: '80px' }}
                >
                  Buscar
                </Button>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>
    </LocalizationProvider>
  )
}